/**
 * Site copy in both languages. English is the source of truth; Hebrew is a
 * translation, not a transliteration — it reads as Hebrew rather than as
 * English word order in Hebrew letters.
 *
 * Deliberately NOT translated: product and tool names (Claude Code, Cursor,
 * MCP, SDD), technology names, article titles (they link to English posts),
 * and the SDD demo's artifacts (specs, diffs and code are written in English
 * in the real framework too).
 */

export const STRINGS = {
  en: {
    nav: {
      about: "About",
      projects: "Projects",
      agentStack: "Agent Stack",
      experience: "Experience",
      skills: "Skills",
      articles: "Articles",
      resume: "Resume",
      liveDemo: "Live demo",
      viewingAs: "Viewing as",
      language: "Language",
    },

    picker: {
      eyebrow: "Before you start",
      title: "Who's reading?",
      subtitle: "Same career, three ways of telling it. Pick the one that fits — you can switch at any time from the top bar.",
      cta: "View this way",
      hrLabel: "Recruiter / HR",
      hrLine: "Light theme. Keywords, not paragraphs.",
      hrDetail: "Role, scale, stack and education at a glance — built for a 60-second screen.",
      techLabel: "Engineer",
      techLine: "Dark theme. Full depth.",
      techDetail: "The SDD framework, the agent stack and the architecture, with the reasoning intact.",
      execLabel: "Executive / Founder",
      execLine: "Outcomes and scale.",
      execDetail: "What was built, what it changed, and what it takes to run an R&D org this size.",
    },

    hero: {
      morning: "Good morning",
      afternoon: "Good afternoon",
      evening: "Good evening",
      night: "Still up?",
      greetingTail: "— thanks for stopping by.",
      badge: "VP R&D · KabatOne",
      name: "I'm Ben Mishali —",
      title: "AI-Driven Development Leader.",
      blurb: "I build the frameworks that let engineering orgs ship with AI — including the Spec-Driven Development (SDD) framework now used across a 40-person global R&D org in Israel and Mexico, building public-safety and command-and-control platforms. BSc Computer Engineering · MBA.",
      tags: ["Spec-Driven Development", "Agentic Workflows", "VP R&D", "Full-Stack"],
      agentStack: "Agent Stack",
      viewProjects: "View Projects",
      viewExperience: "View Experience",
      learnMore: "Learn More",
      downloadResume: "Download Resume",
      contactMe: "Contact Me",
      findMe: "Find me on",
      scroll: "SCROLL",
      yearsExp: "Years Exp.",
      personRnd: "Person R&D",
    },

    about: {
      eyebrow: "About Me",
      headlineTech: "VP R&D who still builds",
      headlineHR: "VP R&D · AI-Driven Development",
      headlineExec: "Engineering leadership that ships",
      introTech: "I lead a 40-person R&D organization across Israel and Mexico building public-safety and command-and-control platforms — and I built the Spec-Driven Development framework the whole org now ships through. Engineering depth with an MBA on top.",
      introHR: "40-person global R&D organization · Israel + Mexico · 9+ years engineering · 30–40% hands-on · builder of the Spec-Driven Development framework · BSc Computer Engineering · MBA.",
      introExec: "I run a 40-person R&D organization across two countries and own its technical strategy, roadmap and budget. I also built the delivery framework the org ships through — so the leverage is structural, not personal.",
      factRole: "Role",
      factRoleValue: "VP R&D · 30–40% hands-on",
      factTeam: "Team",
      factTeamValue: "40 engineers · Israel + Mexico",
      factExperience: "Experience",
      factExperienceValue: "9+ years engineering",
      factEducation: "Education",
      factEducationValue: "BSc Computer Eng. · MBA",
      factFocus: "Focus",
      factFocusValue: "AI-driven development",
      factStack: "Stack",
      factStackValue: "TS · Angular · React · .NET · C++",
    },

    metrics: {
      eyebrow: "Impact by the numbers",
      title: "AI-native engineering, at org scale",
      items: [
        { label: "Engineers on SDD", desc: "Rolled out across the whole R&D org" },
        { label: "Agents per Ticket", desc: "Intake through review, three human gates" },
        { label: "Years of Experience", desc: "Embedded C++/Qt to AI-native workflows" },
        { label: "Countries", desc: "Israel and Mexico, plus outsourced teams" },
        { label: "Articles Published", desc: "Architecture, Node.js and AI-driven dev" },
        { label: "Stack Layers", desc: "SDD · Claude Code · Cursor · MCP" },
      ],
    },

    projects: {
      eyebrow: "Featured Work",
      title: "Building the way software gets built",
      subtitle: "A Spec-Driven Development framework, org-wide AI tooling adoption, and the public-safety platforms they ship.",
      problem: "Problem",
      solution: "Solution",
      impact: "Business Impact",
      architecture: "Architecture",
      showDetails: "View impact & architecture",
      hideDetails: "Hide details",
    },

    agentStack: {
      eyebrow: "The Agent Stack",
      title: "No single agent runs the whole job",
      subtitle: "These tools aren't competitors — they're layers. Picking the right layer for each task is the whole discipline.",
      whyShape: "Why this shape",
      whyBody: "The 2026 landscape has no shortage of capable agents. Three criteria decided it: does it run unattended on a large task, does it leave a reviewable artifact, does it read the same context everything else reads. Two layers that clear that bar beat five that overlap.",
      reference: "Landscape reference",
      flowEyebrow: "Agentic Workflows",
      flowTitle: "One ticket, seven agents, three human gates",
      flowSubtitle: "An agentic workflow isn't a longer prompt — it's a pipeline. Each stage runs in its own context, hands a concrete artifact to the next, and stops where human judgement changes the outcome.",
      humanGate: "Human gate",
      demoTitle: "Don't take the diagram's word for it — run a ticket",
      demoBody: "Watch the pipeline execute end to end: read the spec it writes, the plan the simplifier cuts down, the diff, and the defect the review agent catches.",
      demoCta: "Open the demo",
    },

    experience: {
      eyebrow: "Career Journey",
      title: "From real-time C++ to AI-native R&D",
      subtitle: "9+ years from embedded real-time systems to leading a 40-person global R&D org built around AI-driven development.",
      currentRole: "Current Role",
      promotion: "Promotion",
      education: "Education",
    },

    skills: {
      eyebrow: "Technical Expertise",
      title: "AI-native, full-stack, embedded to cloud",
      subtitle: "Spec-driven agentic workflows on top of 9+ years of full-stack depth — C++/Qt, .NET, Angular, React, Node.js and DevOps.",
      proficiency: "Proficiency",
    },

    articles: {
      eyebrow: "Writing",
      title: "Technical articles on Medium",
      subtitle: "10 published articles on agentic development, Node.js, architecture patterns, testing, and frontend frameworks.",
      readOn: "Read on Medium →",
      newTag: "New",
    },

    latest: {
      badge: "Latest article",
      cta: "Read it on Medium",
      dismiss: "Dismiss",
    },

    contact: {
      eyebrow: "Get In Touch",
      titleA: "Let's build something",
      titleB: "exceptional",
      subtitle: "Open to strategic engineering leadership roles, advisory positions, and interesting technical challenges. If you're building something ambitious, let's talk.",
      sendEmail: "Send an Email",
      downloadResume: "Download Resume",
      copied: "✓ Copied!",
      linkedinDesc: "Connect professionally",
      githubDesc: "See my code",
      mediumDesc: "Read my articles",
      emailDesc: "Send a message",
    },

    footer: {
      role: "VP of R&D · AI-Driven Development · Full-Stack",
      rights: "All rights reserved.",
    },
  },

  // ───────────────────────────────────────────────────────────────────────
  he: {
    nav: {
      about: "אודות",
      projects: "פרויקטים",
      agentStack: "מערך הסוכנים",
      experience: "ניסיון",
      skills: "כישורים",
      articles: "מאמרים",
      resume: "קורות חיים",
      liveDemo: "הדגמה חיה",
      viewingAs: "צפייה בתור",
      language: "שפה",
    },

    picker: {
      eyebrow: "רגע לפני שמתחילים",
      title: "מי קורא?",
      subtitle: "אותה קריירה, שלוש דרכים לספר אותה. בחרו את זו שמתאימה — אפשר להחליף בכל רגע מהסרגל העליון.",
      cta: "הצג כך",
      hrLabel: "גיוס / משאבי אנוש",
      hrLine: "עיצוב בהיר. מילות מפתח, לא פסקאות.",
      hrDetail: "תפקיד, היקף, טכנולוגיות והשכלה במבט אחד — בנוי לסינון של דקה.",
      techLabel: "מהנדס/ת",
      techLine: "עיצוב כהה. לעומק.",
      techDetail: "מסגרת ה-SDD, מערך הסוכנים והארכיטקטורה — כולל השיקולים שמאחוריהם.",
      execLabel: "הנהלה / יזמים",
      execLine: "תוצאות והיקף.",
      execDetail: "מה נבנה, מה זה שינה, ומה נדרש כדי לנהל ארגון מו\"פ בגודל כזה.",
    },

    hero: {
      morning: "בוקר טוב",
      afternoon: "צהריים טובים",
      evening: "ערב טוב",
      night: "עוד ערים?",
      greetingTail: "— תודה שקפצתם.",
      badge: "סמנכ\"ל מו\"פ · KabatOne",
      name: "אני בן משעלי —",
      title: "מוביל פיתוח מונחה AI.",
      blurb: "אני בונה את המסגרות שמאפשרות לארגוני פיתוח לעבוד עם AI — ביניהן מסגרת הפיתוח מונחה-המפרט (SDD) שמשמשת היום ארגון מו\"פ גלובלי של 40 איש בישראל ובמקסיקו, בפיתוח פלטפורמות ביטחון הציבור ושליטה ובקרה. בוגר הנדסת מחשבים · MBA.",
      tags: ["פיתוח מונחה מפרט", "תהליכי סוכנים", "סמנכ\"ל מו\"פ", "פול-סטאק"],
      agentStack: "מערך הסוכנים",
      viewProjects: "לפרויקטים",
      viewExperience: "לניסיון המקצועי",
      learnMore: "מידע נוסף",
      downloadResume: "הורדת קורות חיים",
      contactMe: "צרו קשר",
      findMe: "מצאו אותי ב־",
      scroll: "גלילה",
      yearsExp: "שנות ניסיון",
      personRnd: "אנשי מו\"פ",
    },

    about: {
      eyebrow: "אודות",
      headlineTech: "סמנכ\"ל מו\"פ שעדיין כותב קוד",
      headlineHR: "סמנכ\"ל מו\"פ · פיתוח מונחה AI",
      headlineExec: "מנהיגות הנדסית שמספקת תוצרים",
      introTech: "אני מוביל ארגון מו\"פ של 40 איש בישראל ובמקסיקו, שבונה פלטפורמות ביטחון הציבור ושליטה ובקרה — ובניתי את מסגרת הפיתוח מונחה-המפרט שדרכה הארגון כולו מספק היום. עומק הנדסי לצד MBA.",
      introHR: "ארגון מו\"פ גלובלי של 40 איש · ישראל + מקסיקו · 9+ שנות ניסיון · 30–40% עבודה מעשית · בונה מסגרת הפיתוח מונחה-המפרט · הנדסת מחשבים · MBA.",
      introExec: "אני מנהל ארגון מו\"פ של 40 איש בשתי מדינות ואחראי על האסטרטגיה הטכנולוגית, מפת הדרכים והתקציב. במקביל בניתי את מסגרת הפיתוח שדרכה הארגון מספק — כך שהמינוף מובנה בתהליך, ולא תלוי בי.",
      factRole: "תפקיד",
      factRoleValue: "סמנכ\"ל מו\"פ · 30–40% מעשי",
      factTeam: "צוות",
      factTeamValue: "40 מהנדסים · ישראל + מקסיקו",
      factExperience: "ניסיון",
      factExperienceValue: "9+ שנות הנדסה",
      factEducation: "השכלה",
      factEducationValue: "הנדסת מחשבים · MBA",
      factFocus: "מיקוד",
      factFocusValue: "פיתוח מונחה AI",
      factStack: "טכנולוגיות",
      factStackValue: "TS · Angular · React · .NET · C++",
    },

    metrics: {
      eyebrow: "המספרים",
      title: "הנדסה מונחית AI, בקנה מידה ארגוני",
      items: [
        { label: "מהנדסים ב-SDD", desc: "הוטמע ברוחב כל ארגון המו\"פ" },
        { label: "סוכנים לכל משימה", desc: "מקליטה ועד סקירה, שלושה שערים אנושיים" },
        { label: "שנות ניסיון", desc: "מ-C++/Qt מוטמע ועד תהליכי AI" },
        { label: "מדינות", desc: "ישראל ומקסיקו, בתוספת צוותי מיקור חוץ" },
        { label: "מאמרים שפורסמו", desc: "ארכיטקטורה, Node.js ופיתוח מונחה AI" },
        { label: "שכבות במערך", desc: "SDD · Claude Code · Cursor · MCP" },
      ],
    },

    projects: {
      eyebrow: "עבודות נבחרות",
      title: "בונה את הדרך שבה נבנית תוכנה",
      subtitle: "מסגרת פיתוח מונחה-מפרט, הטמעת כלי AI ברוחב הארגון, והפלטפורמות שהם מספקים.",
      problem: "הבעיה",
      solution: "הפתרון",
      impact: "השפעה עסקית",
      architecture: "ארכיטקטורה",
      showDetails: "הצג השפעה וארכיטקטורה",
      hideDetails: "הסתר פרטים",
    },

    agentStack: {
      eyebrow: "מערך הסוכנים",
      title: "אף סוכן בודד לא מריץ את כל העבודה",
      subtitle: "הכלים האלה אינם מתחרים — הם שכבות. בחירת השכבה הנכונה לכל משימה היא כל התורה.",
      whyShape: "למה דווקא כך",
      whyBody: "בנוף של 2026 לא חסרים סוכנים טובים. שלושה קריטריונים הכריעו: האם הוא רץ ללא פיקוח על משימה גדולה, האם הוא מותיר תוצר בר-סקירה, והאם הוא קורא את אותו הקשר שכל השאר קוראים. שתי שכבות שעומדות ברף עדיפות על חמש שחופפות.",
      reference: "סקירת הנוף",
      flowEyebrow: "תהליכי סוכנים",
      flowTitle: "משימה אחת, שבעה סוכנים, שלושה שערים אנושיים",
      flowSubtitle: "תהליך סוכנים אינו פרומפט ארוך יותר — הוא צנרת. כל שלב רץ בהקשר משלו, מעביר תוצר מוחשי לשלב הבא, ונעצר היכן ששיקול דעת אנושי משנה את התוצאה.",
      humanGate: "שער אנושי",
      demoTitle: "אל תסתמכו על התרשים — הריצו משימה",
      demoBody: "צפו בצנרת רצה מקצה לקצה: המפרט שהיא כותבת, התוכנית שסוכן הפישוט מקצץ, הדיף, והתקלה שסוכן הסקירה תופס.",
      demoCta: "פתחו את ההדגמה",
    },

    experience: {
      eyebrow: "מסלול מקצועי",
      title: "מ-C++ בזמן אמת למו\"פ מונחה AI",
      subtitle: "9+ שנים ממערכות זמן אמת מוטמעות ועד הובלת ארגון מו\"פ גלובלי של 40 איש הבנוי סביב פיתוח מונחה AI.",
      currentRole: "תפקיד נוכחי",
      promotion: "קידום",
      education: "השכלה",
    },

    skills: {
      eyebrow: "מומחיות טכנית",
      title: "מונחה AI, פול-סטאק, ממוטמע ועד ענן",
      subtitle: "תהליכי סוכנים מונחי-מפרט על גבי 9+ שנות עומק פול-סטאק — C++/Qt, ‏.NET, ‏Angular, ‏React, ‏Node.js ו-DevOps.",
      proficiency: "רמת שליטה",
    },

    articles: {
      eyebrow: "כתיבה",
      title: "מאמרים טכניים ב-Medium",
      subtitle: "10 מאמרים שפורסמו על פיתוח מונחה-סוכנים, Node.js, דפוסי ארכיטקטורה, בדיקות ומסגרות צד-לקוח.",
      readOn: "קראו ב-Medium ←",
      newTag: "חדש",
    },

    latest: {
      badge: "המאמר האחרון",
      cta: "לקריאה ב-Medium",
      dismiss: "סגירה",
    },

    contact: {
      eyebrow: "יצירת קשר",
      titleA: "בואו נבנה משהו",
      titleB: "יוצא דופן",
      subtitle: "פתוח לתפקידי הובלה הנדסית, ייעוץ ואתגרים טכניים מעניינים. אם אתם בונים משהו שאפתני — נדבר.",
      sendEmail: "שליחת מייל",
      downloadResume: "הורדת קורות חיים",
      copied: "✓ הועתק!",
      linkedinDesc: "התחברות מקצועית",
      githubDesc: "הקוד שלי",
      mediumDesc: "המאמרים שלי",
      emailDesc: "שליחת הודעה",
    },

    footer: {
      role: "סמנכ\"ל מו\"פ · פיתוח מונחה AI · פול-סטאק",
      rights: "כל הזכויות שמורות.",
    },
  },
};
